<template>
  <div>
    <payment-link-layout
      display="flex"
      marginLeft="36px"
      footerBackgroundColor="white"
      :hideSecurityWarning="false"
      hideSecurityWarningOnMobile
      fullHeight
      v-if="!fetching && !loading && insuranceOptions"
    >
      <template v-slot:content>
        <div id="onboarding-insurance" class="insurance-page">
          <div class="insurance-container">
            <div class="step-description">
              <h1 class="title">Contrato de financiamento da compra</h1>
            </div>
            <div class="insurance-block">
              <InsuranceOptions
                v-model="insurance"
                @change="setSkeleton"
                :options="insuranceOptions"
              />
            </div>
            <div class="payment-block">
              <ul class="payment-options">
                <li
                  v-if="calculatedOptions.downPaymentValue !== 0"
                  class="option"
                  :class="{ skeleton: skeleton }"
                >
                  <span class="option-title" :style="{ visibility: !skeleton }">{{
                    downPaymentText
                  }}</span>
                  <span v-if="!skeleton" class="option-value">{{ formattedDownPayment }}</span>
                </li>
                <li class="option" :class="{ skeleton: skeleton }">
                  <span class="option-title" :style="{ visibility: !skeleton }"
                    >Parcelas {{ insurance ? '+ Seguro' : null }}</span
                  >
                  <span v-if="!skeleton" class="option-value">{{
                    `${installmentsNumber}x de
                  ${installmentValue}`
                  }}</span>
                </li>
                <li style="display: flex; flex-direction: column; width: 100%">
                  <div class="option" :class="{ skeleton: skeleton }">
                    <span class="option-title">Valor total</span>
                    <span v-if="!skeleton" class="option-value">{{ formattedTotalAmount }}</span>
                  </div>
                </li>
              </ul>
              <div :style="{ marginBottom: isMobile ? '24px' : 0 }">
                <Button class="continue-button" color="primary" @click="actionEvent">
                  {{ 'Continuar' }}
                </Button>
                <security-warning v-if="isMobile" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </payment-link-layout>
    <loading-page v-if="loading || fetching" :isFullPage="true" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StringMixin from '@/mixins/string';
import InsuranceOptions from '@/feature/onboarding/components/InsuranceOptions.vue';
import Button from '@/components/ui/AppButton.vue';
import moment from '@/lib/moment-ptbr';
import SecurityWarning from '@/feature/paymentLink/components/SecurityWarning.vue';
import PaymentLinkLayout from '@/feature/paymentLink/layout/PaymentLinkLayout.vue';
import LoadingPage from '@/feature/paymentLink/components/LoadingPage.vue';

export default {
  name: 'onboarding-insurance',
  data: () => ({
    insurance: true,
    skeleton: false,
    fetching: true,
    loading: false,
  }),
  mixins: [StringMixin],
  components: { Button, InsuranceOptions, LoadingPage, PaymentLinkLayout, SecurityWarning },
  async mounted() {
    this.fetching = true;
    const yearsOld = moment().diff(this.getBirthdate, 'years');

    if (yearsOld >= 70) {
      this.fetching = true;
      await this.sendInsuranceConfirmation(false);
      await this.handleNextStep();
    } else {
      if (this.onboarding) {
        await this.fetchInsuranceOptions();
      }
      this.fetching = false;
    }
  },
  computed: {
    ...mapGetters('onboardingV2', [
      'onboarding',
      'transaction',
      'insuranceOptions',
      'getBirthdate',
    ]),
    calculatedOptions() {
      return this.insurance
        ? this.insuranceOptions?.insuranceOption
        : this.insuranceOptions?.originalOption;
    },
    isMobile() {
      return window.innerWidth < 768;
    },
    fullName() {
      if (!this.onboarding.name) return;
      const name = this.onboarding.name.split(' ');
      // eslint-disable-next-line consistent-return
      return name[0] + name[name.length - 1];
    },
    formattedDate() {
      const date = new Date(this.onboarding.createdAt);
      // eslint-disable-next-line consistent-return
      return date.toLocaleDateString('pt-BR');
    },
    downPaymentText() {
      return this.calculatedOptions?.downPaymentPercentage
        ? `Entrada
                  (${this.calculatedOptions.downPaymentPercentage}%)`
        : 'Entrada';
    },
    formattedDownPayment() {
      return this.calculatedOptions?.downPaymentValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    },
    formattedTotalAmount() {
      return this.calculatedOptions?.totalAmount.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    },
    installmentValue() {
      return this.calculatedOptions?.paymentInstallments.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    },
    installmentsNumber() {
      return this.calculatedOptions?.numberOfInstallments;
    },
  },
  methods: {
    ...mapActions('onboardingV2', ['fetchInsuranceOptions', 'sendInsuranceConfirmation']),
    async actionEvent() {
      this.loading = true;
      try {
        await this.sendInsuranceConfirmation(this.insurance);
        await this.handleNextStep();
      } catch {
        this.$router.replace({
          name: 'v2-onboarding-error',
          query: this.$route.query,
        });
      }
    },
    async handleNextStep() {
      this.$router.replace({
        name: 'v2-onboarding-signature-contract-view',
        query: this.$route.query,
      }).finally(() => {
        this.loading = false;
      });
    },
    setSkeleton() {
      this.skeleton = true;
      setTimeout(() => {
        this.skeleton = false;
      }, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
.skeleton {
  background-color: #e5e7eb;
  animation: pulse 0.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.insurance-page {
  display: flex;
  position: relative;
  flex-direction: column;
  padding-top: 24px;
  height: 100%;

  @media (min-width: 1024px) {
    height: auto;
  }

  .insurance-container {
    display: flex;
    position: relative;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    color: #0f172a;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;

    .step-description {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      align-self: stretch;

      > .title {
        color: var(#0f172a);
        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: 32px;
      }

      > .description {
        color: var(#0f172a);
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
      }
    }

    .insurance-block {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .container-title {
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
      }
    }

    .payment-block {
      bottom: 24px;
      width: 100%;
      display: flex;
      gap: 24px;
      margin-top: auto;
      flex-direction: column;

      @media (min-width: 1024px) {
        margin-top: 0;
      }

      .payment-options {
        display: flex;
        padding: 12px;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        margin-top: auto;
        align-self: stretch;
        border-radius: 6px;
        background: #f1f5f9;

        .option {
          display: flex;
          flex-direction: row;
          width: 100%;

          > .option-title {
            flex-grow: 1;
          }

          > .option-value {
            color: #0f172a;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
          }
        }
      }
    }
  }
}

.continue-button {
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  border-radius: 24px;
  height: 48px;
  border: none !important;
  padding: 12px 24px;
  margin: 0 auto 12px;
  font-weight: 600;
  width: 100%;

  @media (min-width: 1024px) {
    margin: 0 auto;
    width: auto;
  }
}
</style>
